<template>
  <v-container fluid class="mt-16 pa-6">
    <div class="d-flex align-center mb-4">
      <v-icon large color="primary" class="mr-3"
        >mdi-clipboard-check-outline</v-icon
      >
      <h1>Task Management</h1>
    </div>

    <!-- Dashboard Stats -->
    <task-dashboard-stats
      :stats="dashboard_stats"
      :dateFrom="statsDateFrom"
      :dateTo="statsDateTo"
      @view-change="onStatsViewChange"
    />

    <!-- Tabs -->
    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab>Task Assignments</v-tab>
      <v-tab>
        Pending Review
        <v-badge
          v-if="pendingReviewCount > 0"
          :content="pendingReviewCount"
          color="red"
          inline
        ></v-badge>
      </v-tab>
      <v-tab>Recurring Tasks</v-tab>
      <v-tab>History</v-tab>
      <v-tab>Calendar</v-tab>
      <v-tab>Templates</v-tab>
      <v-tab>Daily Report</v-tab>
      <v-tab>User History</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- Tab 1: Task Assignments -->
      <v-tab-item>
        <v-card flat>
          <div class="d-flex align-center mb-3">
            <v-btn color="primary" @click="openCreateTask">
              <v-icon left>mdi-plus</v-icon>
              Assign New Task
            </v-btn>
            <v-spacer></v-spacer>
            <v-select
              v-model="taskStatusFilter"
              :items="taskStatusOptions"
              label="Status"
              clearable
              dense
              outlined
              style="max-width: 200px"
              @change="loadTasks"
              class="ml-3"
            ></v-select>
          </div>

          <v-text-field
            v-model="taskSearch"
            label="Search tasks..."
            prepend-inner-icon="mdi-magnify"
            clearable
            dense
            outlined
            class="mb-2"
          ></v-text-field>

          <v-data-table
            :items="all_tasks"
            :headers="taskHeaders"
            :search="taskSearch"
            :items-per-page="15"
          >
            <template v-slot:item.assigned_to="{ item }">
              {{
                item.assigned_to && typeof item.assigned_to === "object"
                  ? item.assigned_to.full_name
                  : ""
              }}
              <div
                v-if="
                  item.assigned_to &&
                  typeof item.assigned_to === 'object' &&
                  item.assigned_to.branch
                "
                class="caption grey--text"
              >
                {{ item.assigned_to.branch.branch_name }}
              </div>
            </template>

            <template v-slot:item.priority="{ item }">
              <v-chip :color="priorityColor(item.priority)" small dark>
                {{ priorityLabel(item.priority) }}
              </v-chip>
            </template>

            <template v-slot:item.recurrence_type="{ item }">
              {{ recurrenceLabel(item.recurrence_type) }}
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                :color="
                  item.status === 'active'
                    ? 'green'
                    : item.status === 'paused'
                    ? 'orange'
                    : 'grey'
                "
                small
                dark
              >
                {{ item.status }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon small color="primary" @click="openEditTask(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                @click="cancelTask(item)"
                v-if="item.status === 'active'"
              >
                <v-icon small>mdi-cancel</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- Tab 2: Pending Review -->
      <v-tab-item>
        <v-card flat>
          <div class="d-flex align-center mb-3">
            <h3>Tasks Awaiting Acknowledgment</h3>
            <v-spacer></v-spacer>
            <v-btn
              color="green"
              dark
              :disabled="selectedLogs.length === 0"
              :loading="bulkLoading"
              @click="bulkAck"
            >
              <v-icon left>mdi-check-all</v-icon>
              Acknowledge Selected ({{ selectedLogs.length }})
            </v-btn>
          </div>

          <v-data-table
            :items="pendingReviewLogs"
            :headers="reviewHeaders"
            show-select
            v-model="selectedLogs"
            item-key="id"
            :items-per-page="15"
          >
            <template v-slot:item.log_date="{ item }">
              {{ item.log_date ? item.log_date.substr(0, 10) : "" }}
            </template>
            <template v-slot:item.user="{ item }">
              {{
                item.task &&
                item.task.assigned_to &&
                typeof item.task.assigned_to === "object"
                  ? item.task.assigned_to.full_name
                  : ""
              }}
            </template>
            <template v-slot:item.title="{ item }">
              {{ item.task ? item.task.title : "" }}
            </template>
            <template v-slot:item.completed_at="{ item }">
              {{ item.completed_at ? formatDate(item.completed_at) : "-" }}
            </template>
            <template v-slot:item.completion_note="{ item }">
              {{ item.completion_note || "-" }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn small color="green" dark @click="openAcknowledge(item)">
                <v-icon small left>mdi-check</v-icon>
                Acknowledge
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- Tab 3: Recurring Tasks by User -->
      <v-tab-item>
        <task-recurring-by-user @edit="openEditTask" @cancel="cancelTask" />
      </v-tab-item>

      <!-- Tab 4: History -->
      <v-tab-item>
        <task-history-table />
      </v-tab-item>

      <!-- Tab 5: Calendar -->
      <v-tab-item>
        <v-row>
          <v-col cols="12" md="8">
            <task-calendar-view />
          </v-col>
          <v-col cols="12" md="4">
            <div class="mb-3">
              <v-autocomplete
                v-model="progressUserId"
                :items="users"
                item-text="full_name"
                item-value="id"
                label="View User Progress"
                clearable
                dense
                outlined
                @change="loadUserProgress"
              ></v-autocomplete>
            </div>
            <task-user-progress-card :progress="user_progress" />

            <!-- Per-user stats from dashboard -->
            <v-card outlined class="mt-4 pa-3" v-if="perUserStats.length > 0">
              <div class="subtitle-1 font-weight-bold mb-2">
                All Users Overview
              </div>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Done</th>
                    <th>Missed</th>
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="u in perUserStats" :key="u.user_id">
                    <td>{{ u.user_name }}</td>
                    <td class="green--text">{{ u.completed }}</td>
                    <td class="red--text">{{ u.missed }}</td>
                    <td>{{ u.completion_rate }}%</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Tab 6: Templates -->
      <v-tab-item>
        <task-template-list />
      </v-tab-item>

      <!-- Tab 7: Daily Report -->
      <v-tab-item>
        <task-daily-report />
      </v-tab-item>

      <!-- Tab 8: User History -->
      <v-tab-item>
        <task-user-history />
      </v-tab-item>
    </v-tabs-items>

    <!-- Dialogs -->
    <task-assign-dialog
      v-model="showAssignDialog"
      :editTask="editingTask"
      @saved="onTaskSaved"
    />
    <task-acknowledge-dialog
      v-model="showAckDialog"
      :taskLog="ackingLog"
      @acknowledged="onAcknowledged"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TaskDashboardStats from "../components/Task/TaskDashboardStats.vue";
import TaskAssignDialog from "../components/Task/TaskAssignDialog.vue";
import TaskAcknowledgeDialog from "../components/Task/TaskAcknowledgeDialog.vue";
import TaskHistoryTable from "../components/Task/TaskHistoryTable.vue";
import TaskCalendarView from "../components/Task/TaskCalendarView.vue";
import TaskUserProgressCard from "../components/Task/TaskUserProgressCard.vue";
import TaskTemplateList from "../components/Task/TaskTemplateList.vue";
import TaskDailyReport from "../components/Task/TaskDailyReport.vue";
import TaskUserHistory from "../components/Task/TaskUserHistory.vue";
import TaskRecurringByUser from "../components/Task/TaskRecurringByUser.vue";

export default {
  components: {
    TaskDashboardStats,
    TaskAssignDialog,
    TaskAcknowledgeDialog,
    TaskHistoryTable,
    TaskCalendarView,
    TaskUserProgressCard,
    TaskTemplateList,
    // eslint-disable-next-line vue/no-unused-components
    TaskDailyReport,
    // eslint-disable-next-line vue/no-unused-components
    TaskUserHistory,
    TaskRecurringByUser,
  },
  data() {
    return {
      activeTab: 0,
      statsViewType: "daily",
      taskSearch: "",
      taskStatusFilter: "active",
      showAssignDialog: false,
      editingTask: null,
      showAckDialog: false,
      ackingLog: null,
      selectedLogs: [],
      bulkLoading: false,
      progressUserId: null,
      taskHeaders: [
        { text: "Title", value: "title" },
        { text: "Assigned To", value: "assigned_to", width: "160" },
        { text: "Category", value: "category", width: "120" },
        { text: "Priority", value: "priority", width: "110" },
        { text: "Recurrence", value: "recurrence_type", width: "110" },
        { text: "Status", value: "status", width: "100" },
        { text: "Actions", value: "actions", sortable: false, width: "110" },
      ],
      reviewHeaders: [
        { text: "Date", value: "log_date", width: "110" },
        { text: "User", value: "user", width: "150" },
        { text: "Task", value: "title" },
        { text: "Completed At", value: "completed_at", width: "160" },
        { text: "Note", value: "completion_note" },
        { text: "Actions", value: "actions", sortable: false, width: "150" },
      ],
      taskStatusOptions: [
        { text: "Active", value: "active" },
        { text: "Paused", value: "paused" },
        { text: "Cancelled", value: "cancelled" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      dashboard_stats: "reminder/dashboard_stats",
      all_tasks: "reminder/all_tasks",
      task_logs: "reminder/task_logs",
      user_progress: "reminder/user_progress",
      users: "auth/users",
    }),
    pendingReviewLogs() {
      return this.task_logs.filter((l) => l.status === "completed");
    },
    pendingReviewCount() {
      return this.dashboard_stats
        ? this.dashboard_stats.pending_acknowledgment
        : 0;
    },
    perUserStats() {
      return this.dashboard_stats
        ? this.dashboard_stats.per_user_stats || []
        : [];
    },
    statsDateFrom() {
      const today = new Date();
      if (this.statsViewType === "weekly") {
        const day = today.getDay();
        const diff = day === 0 ? 6 : day - 1; // Monday = 0
        const monday = new Date(today);
        monday.setDate(today.getDate() - diff);
        return monday.toISOString().substr(0, 10);
      }
      return today.toISOString().substr(0, 10);
    },
    statsDateTo() {
      if (this.statsViewType === "weekly") {
        const from = new Date(this.statsDateFrom + "T00:00:00");
        const sunday = new Date(from);
        sunday.setDate(from.getDate() + 6);
        return sunday.toISOString().substr(0, 10);
      }
      return new Date().toISOString().substr(0, 10);
    },
  },
  created() {
    this.loadDashboard();
    this.loadTasks();
    this.loadReviewLogs();
    if (!this.users || this.users.length === 0) {
      this.get_users();
    }
  },
  methods: {
    ...mapActions({
      get_dashboard_stats: "reminder/get_dashboard_stats",
      get_all_tasks: "reminder/get_all_tasks",
      get_recurring_tasks: "reminder/get_recurring_tasks",
      get_task_logs: "reminder/get_task_logs",
      delete_task: "reminder/delete_task",
      bulk_acknowledge: "reminder/bulk_acknowledge",
      get_user_progress: "reminder/get_user_progress",
      get_users: "auth/get_users",
      set_notification: "notification/set_notification",
    }),
    loadDashboard() {
      this.get_dashboard_stats({
        date_from: this.statsDateFrom,
        date_to: this.statsDateTo,
      });
    },
    onStatsViewChange(type) {
      this.statsViewType = type;
      this.$nextTick(() => {
        this.loadDashboard();
      });
    },
    loadTasks() {
      const filters = {};
      if (this.taskStatusFilter) filters.status = this.taskStatusFilter;
      this.get_all_tasks(filters);
    },
    loadReviewLogs() {
      this.get_task_logs({ status: "completed" });
    },
    loadUserProgress() {
      if (this.progressUserId) {
        this.get_user_progress(this.progressUserId);
      }
    },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleString();
    },
    priorityColor(p) {
      return p === "very_urgent" ? "red" : p === "urgent" ? "orange" : "blue";
    },
    priorityLabel(p) {
      return p === "very_urgent"
        ? "Very Urgent"
        : p === "urgent"
        ? "Urgent"
        : "Basic";
    },
    recurrenceLabel(r) {
      const map = {
        none: "One-Time",
        daily: "Daily",
        weekly: "Weekly",
        monthly: "Monthly",
      };
      return map[r] || r;
    },
    openCreateTask() {
      this.editingTask = null;
      this.showAssignDialog = true;
    },
    openEditTask(task) {
      this.editingTask = task;
      this.showAssignDialog = true;
    },
    async cancelTask(task) {
      if (
        await this.$confirm({
          title: "Cancel Task",
          message: "Cancel this task assignment?",
          color: "red",
          icon: "mdi-delete",
        })
      ) {
        this.delete_task(task.id).then(() => {
          this.set_notification({
            message: "Task cancelled successfully!",
            type: "success",
          });
          this.loadTasks();
        });
      }
    },
    openAcknowledge(log) {
      this.ackingLog = log;
      this.showAckDialog = true;
    },
    onTaskSaved() {
      this.loadTasks();
      this.loadDashboard();
      this.get_recurring_tasks();
    },
    onAcknowledged() {
      this.loadReviewLogs();
      this.loadDashboard();
    },
    bulkAck() {
      if (this.selectedLogs.length === 0) return;
      this.bulkLoading = true;
      this.bulk_acknowledge({
        logIds: this.selectedLogs.map((l) => l.id),
        admin_comment: "",
      })
        .then(() => {
          this.bulkLoading = false;
          this.set_notification({
            message:
              this.selectedLogs.length + " task(s) acknowledged successfully!",
            type: "success",
          });
          this.selectedLogs = [];
          this.loadReviewLogs();
          this.loadDashboard();
        })
        .catch((err) => {
          this.bulkLoading = false;
          this.$alert({
            title: "Error",
            message: err.response ? err.response.data.error : err.message,
            color: "error",
            icon: "mdi-alert-circle",
          });
        });
    },
  },
};
</script>
